import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { buildStampImageAlt } from '../api/productos';
import './PosterOptionsForm.css';
import backOrnamentImage from '../assets/back-ornament.png';

const DESKTOP_MIN_WIDTH = 769;
const MIN_BACK_FIT_SCALE = 0.5;
const MAX_BACK_FIT_SCALE = 1.3;

const FALLBACK_PRAYER = 'Ángel de la guarda, dulce compañía, no me desampares ni de noche ni de día.';

const BACK_ORNAMENT_IMAGE = backOrnamentImage;

const BACK_STYLE_BY_CATEGORY = {
  Bautismo: {
    text: '#6F5A2A',
    background: '#f6f6f3',
  },
  Comunion: {
    text: '#5A4D2A',
    background: '#f7f6f1',
  },
  Confirmacion: {
    text: '#5E4F32',
    background: '#f5f4ee',
  },
};

const BACK_TITLE_BY_CATEGORY = {
  Bautismo: 'Recuerdo de mi Bautismo',
  Comunion: 'Recuerdo de mi Comunión',
  Confirmacion: 'Recuerdo de mi Confirmación',
};

const formatDate = (value) => {
  if (!value) return '';

  const normalized = value.includes('T') ? value : `${value}T00:00:00`;
  const parsedDate = new Date(normalized);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString('es-UY', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const buildCelebrationLine = (category, genero, date, capilla) => {
  if (!date && !capilla) return '';

  const segments = [];

  if (date) {
    const verb =
      category === 'Comunion'
        ? `Recibí mi Primera Comunión el día ${formatDate(date)}`
        : `Fui ${genero === 'nino' ? 'bautizado' : 'bautizada'} el ${formatDate(date)}`;
    segments.push(verb);
  }

  if (capilla) {
    segments.push(`en la ${capilla}`);
  }

  return `${segments.join(' ')}.`;
};

const PosterOptionsForm = ({
  poster,
  stampFormData,
  printSide,
}) => {
  const isDoubleSide = printSide === 'double';
  const palette = BACK_STYLE_BY_CATEGORY[poster.category] || BACK_STYLE_BY_CATEGORY.Bautismo;
  const backTitle = BACK_TITLE_BY_CATEGORY[poster.category] || BACK_TITLE_BY_CATEGORY.Bautismo;
  const fullName = (stampFormData.name || 'Nombre Apellido')
    .trim()
    .replace(/\s+/g, ' ');
  const prayerText = stampFormData.text?.trim() || FALLBACK_PRAYER;
  const celebrationLine = buildCelebrationLine(
    poster.category,
    stampFormData.genero,
    stampFormData.date,
    stampFormData.capilla,
  );

  const backRef = useRef(null);
  const [backFitScale, setBackFitScale] = useState(1);

  // On desktop the Dorso card must be exactly as tall as the fixed-ratio
  // Frente card, so instead of letting content overflow/scroll, the text
  // size is scaled to whatever fits (or fills) that fixed height. Mobile
  // already handles this by letting the card grow, so it's left alone here.
  useLayoutEffect(() => {
    const el = backRef.current;
    if (!el) return undefined;

    const measureAndApplyScale = () => {
      if (window.innerWidth < DESKTOP_MIN_WIDTH) return;

      const previousScale = el.style.getPropertyValue('--back-fit-scale');
      el.style.setProperty('--back-fit-scale', '1');
      const available = el.clientHeight;
      const needed = el.scrollHeight;
      el.style.setProperty('--back-fit-scale', previousScale || '1');

      if (!needed || !available) return;

      const rawScale = available / needed;
      const clampedScale = Math.min(
        Math.max(rawScale, MIN_BACK_FIT_SCALE),
        MAX_BACK_FIT_SCALE,
      );
      setBackFitScale(Math.round(clampedScale * 1000) / 1000);
    };

    measureAndApplyScale();
    window.addEventListener('resize', measureAndApplyScale);
    return () => window.removeEventListener('resize', measureAndApplyScale);
  }, [poster, stampFormData, printSide]);

  const renderBackContent = (compact = false) => (
    <div
      ref={compact ? null : backRef}
      className={`custom-back custom-back-reference${compact ? ' custom-back-reference--compact' : ''}`}
      style={{
        '--back-text': palette.text,
        '--back-background': palette.background,
        ...(compact ? {} : { '--back-fit-scale': backFitScale }),
      }}
    >
      <p className="back-prayer">{prayerText}</p>

      <div className="back-ornament" aria-hidden="true">
        <img src={BACK_ORNAMENT_IMAGE} alt="" className="back-ornament-image" />
      </div>

      <h3 className="back-name">{fullName.toUpperCase()}</h3>

      {celebrationLine && <p className="back-description">{celebrationLine}</p>}

      {(stampFormData.madrina || stampFormData.padrino) && (
        <div className="back-sponsors">
          <p className="back-sponsors-title">Mis Padrinos:</p>
          {stampFormData.madrina && <p>{stampFormData.madrina}</p>}
          {stampFormData.padrino && <p>{stampFormData.padrino}</p>}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="poster-form-wrapper">
        <form className="poster-form" onSubmit={(event) => event.preventDefault()}>
          {isDoubleSide ? (
            <div className="poster-preview-double">
              <div className="poster-side">
                <p className="side-label">Frente</p>
                <div className="poster-preview-front">
                  <p className="front-title">{backTitle}</p>
                  <img src={poster.image} alt={buildStampImageAlt(poster)} className="poster-preview-image" />
                </div>
              </div>

              <div className="poster-side back-side">
                <p className="side-label">Dorso</p>
                {renderBackContent()}
              </div>
            </div>
          ) : (
            <div className="poster-preview-single">
              <div className="poster-side single-side">
                <p className="side-label">Cara única</p>
                <img src={poster.image} alt={buildStampImageAlt(poster)} className="poster-preview-image" />
                {renderBackContent(true)}
              </div>
            </div>
          )}
        </form>
      </div>
    </>
  );
};

PosterOptionsForm.propTypes = {
  poster: PropTypes.shape({
    id: PropTypes.number.isRequired,
    category: PropTypes.string,
    title: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        description: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  stampFormData: PropTypes.shape({
    genero: PropTypes.string,
    name: PropTypes.string,
    date: PropTypes.string,
    text: PropTypes.string,
    capilla: PropTypes.string,
    madrina: PropTypes.string,
    padrino: PropTypes.string,
  }).isRequired,
  printSide: PropTypes.string.isRequired,
};

export default PosterOptionsForm;
